/** Small DOM helpers shared by the widgets. */

export function $<T extends Element = HTMLElement>(
  root: ParentNode,
  selector: string,
): T | null {
  return root.querySelector<T>(selector);
}

export function $$<T extends Element = HTMLElement>(
  root: ParentNode,
  selector: string,
): T[] {
  return Array.from(root.querySelectorAll<T>(selector));
}

/** Sets text only when it changed, so a tick touches as little as it can. */
export function text(el: Element | null, value: string): void {
  if (el && el.textContent !== value) el.textContent = value;
}

export function attr(el: Element | null, name: string, value: string): void {
  if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
}

/**
 * SegmentedControl: a group of aria-pressed buttons, one pressed at a time.
 * Returns a setter so other controls (a slider, a bar) can move it too.
 */
export function segmented(
  group: Element,
  onChange: (value: string) => void,
): (value: string | null) => void {
  const buttons = $$<HTMLButtonElement>(group, 'button[data-value]');
  const set = (value: string | null) => {
    for (const b of buttons) {
      attr(b, 'aria-pressed', String(b.dataset.value === value));
    }
  };
  for (const b of buttons) {
    b.addEventListener('click', () => {
      set(b.dataset.value ?? null);
      onChange(b.dataset.value ?? '');
    });
  }
  return set;
}

/**
 * The ARIA radio group pattern: a click, Space, or an arrow key checks a
 * radio, and only the checked one is in the tab order. `onCheck` hears each
 * change, with the radio's data-value.
 */
export function radiogroup(
  group: Element,
  onCheck: (value: string) => void,
): void {
  const radios = $$<HTMLElement>(group, '[role="radio"]');
  const check = (index: number) => {
    if (radios[index].getAttribute('aria-checked') === 'true') return;
    radios.forEach((r, i) => {
      attr(r, 'aria-checked', String(i === index));
      r.tabIndex = i === index ? 0 : -1;
    });
    onCheck(radios[index].dataset.value ?? '');
  };
  radios.forEach((radio, i) => {
    radio.addEventListener('click', () => check(i));
    radio.addEventListener('keydown', (e) => {
      const keys: Record<string, number> = {
        ArrowRight: 1,
        ArrowDown: 1,
        ArrowLeft: -1,
        ArrowUp: -1,
      };
      if (!(e.key in keys)) return;
      e.preventDefault();
      const next = (i + keys[e.key] + radios.length) % radios.length;
      radios[next].focus();
      check(next);
    });
  });
}

/**
 * The ARIA tabs pattern: arrow keys move between tabs and select them, and
 * only the selected tab is in the tab order. With `manual`, the arrow keys
 * only move focus, and Enter or Space (the button's own click) selects.
 */
export function tablist(
  list: Element,
  onSelect: (index: number, byUser: boolean) => void,
  options: { manual?: boolean } = {},
): (index: number) => void {
  const tabs = $$<HTMLElement>(list, '[role="tab"]');
  const select = (index: number) => {
    tabs.forEach((t, i) => {
      attr(t, 'aria-selected', String(i === index));
      t.tabIndex = i === index ? 0 : -1;
    });
  };
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => {
      select(i);
      onSelect(i, true);
    });
    tab.addEventListener('keydown', (e) => {
      const keys: Record<string, number> = {
        ArrowRight: 1,
        ArrowDown: 1,
        ArrowLeft: -1,
        ArrowUp: -1,
      };
      let next: number | null = null;
      if (e.key in keys) next = (i + keys[e.key] + tabs.length) % tabs.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = tabs.length - 1;
      if (next === null) return;
      e.preventDefault();
      tabs[next].focus();
      if (options.manual) return;
      select(next);
      onSelect(next, true);
    });
  });
  return select;
}
